import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Button } from '~/atomic/atom/Button';
import Modal from '~/atomic/molecula/Modal';
import { PageTitleStyle } from '~/atomic/Typography';
import { getMethodsThunk } from '~/feature/finance/BuyingSteps/ChoosePay/choosePay.data';
import { choosePaySelector } from '~/feature/finance/BuyingSteps/ChoosePay/choosePay.selector';
import { choosePayActions } from '~/feature/finance/BuyingSteps/ChoosePay/choosePay.slice';
import { ConfirmContactModal } from '~/feature/finance/BuyingSteps/ChoosePay/ConfirmContactModal';
import { BankCardMethodPaymentPublicProps } from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/BankCardMethodPayment/BankCardMethodPayment';
import {
  MethodPaymentsList,
  MethodPaymentsListPublicProps,
} from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/MethodPaymentsList';
import { userSelector } from '~/feature/user/user.selector';
import { ymOnceForBook } from '~/feature/yandex/ymOnceForBook';
import {
  getStore, useAppDispatch, useAppSelector,
} from '~/store';

interface ChoosePayModalProps extends BankCardMethodPaymentPublicProps, MethodPaymentsListPublicProps {
  buyHandler: () => void;
  back: () => void;
  closeModal: () => void;
  isOpen: boolean;
  discountPrice: number;
}

export const ChoosePayModal = ({
  back,
  isOpen,
  closeModal,
  buyHandler,
  discountPrice,
  isShowSaveCardConfirm = true,
  showMethods,
}: ChoosePayModalProps) => {
  const dispatch = useAppDispatch();
  const { isPayLoading } = useAppSelector(choosePaySelector);
  const { user } = useAppSelector(userSelector);

  const needsContact = !user.email && !user.phone;

  useEffect(() => {
    if (isOpen) {
      dispatch(getMethodsThunk());
    } else {
      dispatch(choosePayActions.setIsConfirmContactModal(false));
    }
  }, [isOpen]);

  const changeActiveMethod = async () => {
    // Дедуп по текущей книге: пользователь может несколько раз открывать «Выбор способа
    // оплаты» (закрыл, передумал, ввёл ещё раз) — в воронке это должен быть один шаг.
    // Если модалка открыта не для книги (абонемент/аудиокнига) — currentGraphqlBook=undefined,
    // и ymOnceForBook стреляет без дедупа.
    const currentBookId = getStore().getState().book.currentGraphqlBook?.id;
    ymOnceForBook('choose-method-payment', currentBookId);
    const freshReceiptContact = getStore().getState().choosePay.receiptContact;
    if (needsContact && !freshReceiptContact) {
      dispatch(choosePayActions.setIsConfirmContactModal(true));
    } else {
      await buyHandler();
    }
  };

  const backHandler = () => {
    back();
  };

  return (
    <SCBuyModal
      open={isOpen}
      onCancel={closeModal}
    >
      <SCTitle>
        Выберите способ оплаты
      </SCTitle>
      <MethodPaymentsList
        price={discountPrice}
        changeActiveMethod={changeActiveMethod}
        isPayLoading={isPayLoading}
        isShowSaveCardConfirm={isShowSaveCardConfirm}
        showMethods={showMethods}
      />
      <SCBackButton
        size="large"
        onClick={backHandler}
      >
        Вернуться назад
      </SCBackButton>
      <ConfirmContactModal onOk={buyHandler} />
    </SCBuyModal>
  );
};

const SCBuyModal = styled(Modal)`
  width: 636px !important;
`;

const SCTitle = styled.h3`
  ${PageTitleStyle};
  color: var(--black-color);
  margin-bottom: 20px;
`;

const SCBackButton = styled(Button)` && {
  max-width: 200px;
  width: 100%;
  background-color: var(--white-color);
  margin: 50px auto 0;
}
`;
